/**
 * Preview lightbox for a document search hit (Repository / DocumentsPanel)
 * or an Asset Locker upload (Repository search jump).
 *
 * Loads bytes via the provided `load` callback (BFF download or local
 * blob/GitHub self-heal), then reuses the AssetLocker preview pipeline
 * (`buildDocPreview` + text sniff). When bytes aren't available, falls
 * back to `fallbackText` so the click still does something useful.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import {
  DocsApiError,
  downloadDoc,
  resolveDocHitLocation,
  type SearchHit,
} from "@/lib/docs-api";
import { loadBlob } from "@/lib/blob-store";
import {
  downloadWorkshopBlob,
  isWorkshopBlobSyncEnabled,
} from "@/lib/workshop-blob-sync";
import { mimeFromExt, extFromFilename } from "@/lib/mime-ext";
import {
  binaryFallbackNote,
  buildDocPreview,
  renderDocxInto,
  type SheetPreview,
  type SlidePreview,
} from "@/features/asset-locker/doc-preview";
import type { Upload } from "@/stores/useAssetLocker";

type PreviewKind =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "snippet"; text: string }
  | { kind: "image"; objectUrl: string }
  | { kind: "text"; objectUrl: string; textBody: string }
  | { kind: "pdf"; objectUrl: string }
  | { kind: "docx"; objectUrl: string; docBlob: Blob }
  | { kind: "xlsx"; objectUrl: string; sheets: SheetPreview[] }
  | { kind: "pptx"; objectUrl: string; slides: SlidePreview[] }
  | { kind: "binary"; objectUrl: string; note?: string };

type BlobPreviewDialogProps = {
  title: string;
  /** Optional subtitle (e.g. page number). */
  subtitle?: string;
  /** Hint used when the blob's MIME is blank / octet-stream. */
  mimeHint?: string;
  /** Filename used for the Download link. */
  downloadName?: string;
  /** Resolve the bytes to preview. Return null to show `fallbackText`. */
  load: () => Promise<Blob | null>;
  /** Shown when `load` returns null. */
  fallbackText?: string;
  onClose: () => void;
};

/** Shared lightbox — document search hits and Asset Locker uploads. */
export function BlobPreviewDialog({
  title,
  subtitle,
  mimeHint,
  downloadName,
  load,
  fallbackText = "No preview available for this result.",
  onClose,
}: BlobPreviewDialogProps) {
  const [state, setState] = useState<PreviewKind>({ kind: "loading" });
  const objectUrlRef = useRef<string | null>(null);
  const fileName = downloadName || title || "document";

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const blob = await load();
        if (cancelled) return;
        if (!blob) {
          setState({ kind: "snippet", text: fallbackText });
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        const mimeType =
          (blob.type && blob.type !== "application/octet-stream"
            ? blob.type
            : undefined) ||
          mimeHint ||
          mimeFromExt(extFromFilename(fileName));

        if (mimeType.startsWith("image/")) {
          setState({ kind: "image", objectUrl });
          return;
        }

        const doc = await buildDocPreview(blob, mimeType);
        if (cancelled) return;
        if (doc) {
          setState({ objectUrl, ...doc });
          return;
        }

        const SNIFF_MAX_BYTES = 1_000_000;
        if (blob.size < SNIFF_MAX_BYTES) {
          try {
            const text = await blob.text();
            if (!cancelled && text.length > 0 && !text.includes("\0")) {
              setState({ kind: "text", objectUrl, textBody: text });
              return;
            }
          } catch {
            // fall through to binary
          }
        }

        if (!cancelled) {
          setState({
            kind: "binary",
            objectUrl,
            note: binaryFallbackNote(mimeType),
          });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            err instanceof DocsApiError || err instanceof Error
              ? err.message
              : "Couldn't load this file",
        });
      }
    })();

    return () => {
      cancelled = true;
      const url = objectUrlRef.current;
      if (url) {
        objectUrlRef.current = null;
        queueMicrotask(() => URL.revokeObjectURL(url));
      }
    };
  }, [load, fallbackText, fileName, mimeHint]);

  const objectUrl =
    state.kind === "image" ||
    state.kind === "text" ||
    state.kind === "pdf" ||
    state.kind === "docx" ||
    state.kind === "xlsx" ||
    state.kind === "pptx" ||
    state.kind === "binary"
      ? state.objectUrl
      : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "min(calc(var(--os-vw) * 100 - 48px), 1200px)",
          maxWidth: "calc(var(--os-vw) * 100 - 48px)",
          maxHeight: "calc(var(--os-vh) * 100 - 96px)",
        }}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          Preview of {title}.
        </DialogDescription>
        <div
          className="flex w-full flex-col overflow-hidden rounded-xl"
          style={{
            height: "calc(var(--os-vh) * 100 - 96px)",
            background: "var(--bg-panel)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
            outline: "3px solid #7892A9",
          }}
        >
          <div
            className="flex shrink-0 items-center gap-3 px-4 py-3"
            style={{
              borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
            }}
          >
            <FileText
              aria-hidden
              style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: "var(--fs-base)",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </div>
              {subtitle ? (
                <div
                  style={{
                    fontSize: "var(--fs-micro)",
                    color: "var(--text-muted)",
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>
            {objectUrl && (
              <a
                href={objectUrl}
                download={fileName}
                className="modal-close-btn inline-flex items-center gap-1.5"
                style={{
                  fontSize: "var(--fs-label)",
                  letterSpacing: "1.5px",
                  textDecoration: "none",
                }}
              >
                <Download
                  aria-hidden
                  style={{ width: 11, height: 11 }}
                  strokeWidth={2.5}
                />
                Download
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="modal-close-btn inline-flex items-center gap-1.5"
              style={{ fontSize: "var(--fs-label)", letterSpacing: "1.5px" }}
            >
              <X aria-hidden style={{ width: 11, height: 11 }} strokeWidth={2.5} />
              Close
            </button>
          </div>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            {state.kind === "loading" && (
              <div
                className="flex h-full w-full items-center justify-center gap-2"
                style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
              >
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Loading preview…
              </div>
            )}
            {state.kind === "error" && (
              <div
                className="flex h-full w-full items-center justify-center px-6 text-center"
                style={{ color: "var(--accent-red)", fontSize: "var(--fs-label)" }}
              >
                {state.message}
              </div>
            )}
            {state.kind === "snippet" && (
              <pre
                className="console-scroll h-full w-full overflow-auto p-6"
                style={{
                  background: "var(--bg-panel-alt)",
                  color: "var(--text-secondary)",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "var(--fs-base)",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                }}
              >
                {state.text}
              </pre>
            )}
            {state.kind === "image" && (
              <div
                className="flex h-full w-full items-center justify-center overflow-auto"
                style={{ background: "var(--bg-deep)" }}
              >
                <img
                  src={state.objectUrl}
                  alt={title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            {state.kind === "text" && (
              <pre
                className="console-scroll h-full w-full overflow-auto p-6"
                style={{
                  background: "var(--bg-panel-alt)",
                  color: "var(--text-secondary)",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "var(--fs-base)",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                }}
              >
                {state.textBody}
              </pre>
            )}
            {state.kind === "pdf" && (
              <iframe
                src={state.objectUrl}
                title={`PDF preview of ${title}`}
                className="h-full w-full"
                style={{ border: 0, background: "var(--bg-deep)" }}
              />
            )}
            {state.kind === "docx" && (
              <DocxPreviewPane
                docBlob={state.docBlob}
                label={title}
                objectUrl={state.objectUrl}
                downloadName={fileName}
              />
            )}
            {state.kind === "xlsx" && (
              <SimpleXlsxPane sheets={state.sheets} label={title} />
            )}
            {state.kind === "pptx" && (
              <SimplePptxPane slides={state.slides} label={title} />
            )}
            {state.kind === "binary" && (
              <div
                className="flex h-full w-full flex-col items-center justify-center gap-3 px-6"
                style={{ background: "var(--bg-panel-alt)" }}
              >
                <span
                  className="font-sans uppercase"
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "var(--fs-label)",
                    letterSpacing: "2px",
                    textAlign: "center",
                  }}
                >
                  {state.note ?? "Inline preview not available"}
                </span>
                <a
                  href={state.objectUrl}
                  download={fileName}
                  className="modal-close-btn inline-flex items-center gap-1.5"
                  style={{
                    fontSize: "var(--fs-label)",
                    letterSpacing: "1.5px",
                    textDecoration: "none",
                  }}
                >
                  <Download
                    aria-hidden
                    style={{ width: 11, height: 11 }}
                    strokeWidth={2.5}
                  />
                  Download to view
                </a>
              </div>
            )}
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

type DocumentPreviewDialogProps = {
  hit: SearchHit;
  onClose: () => void;
};

export function DocumentPreviewDialog({ hit, onClose }: DocumentPreviewDialogProps) {
  const load = useCallback(async (): Promise<Blob | null> => {
    const location = resolveDocHitLocation(hit);
    if (!location) return null;
    try {
      return await downloadDoc(location.tier, location.path);
    } catch (err) {
      // Prefer the search snippet over a hard error when download fails.
      if (hit.content?.trim()) return null;
      throw err;
    }
  }, [hit]);

  return (
    <BlobPreviewDialog
      title={hit.filename || "Document preview"}
      subtitle={hit.page ? `Page ${hit.page}` : undefined}
      downloadName={hit.filename || "document"}
      load={load}
      fallbackText={
        hit.content?.trim() || "No preview available for this result."
      }
      onClose={onClose}
    />
  );
}

/** Preview an Asset Locker upload from Repository search (no AssetLocker mount). */
export function UploadPreviewDialog({
  upload,
  onClose,
}: {
  upload: Upload;
  onClose: () => void;
}) {
  const load = useCallback(async (): Promise<Blob | null> => {
    if (upload.blobKey) {
      const cached = await loadBlob(upload.blobKey);
      if (cached) return cached;
    }
    if (upload.githubPath && isWorkshopBlobSyncEnabled()) {
      const fetched = await downloadWorkshopBlob(upload.githubPath);
      if (!fetched) return null;
      // Re-wrap with a known MIME when the adapter returns octet-stream.
      if (upload.mimeType && (!fetched.type || fetched.type === "application/octet-stream")) {
        return new Blob([fetched], { type: upload.mimeType });
      }
      return fetched;
    }
    return null;
  }, [upload]);

  return (
    <BlobPreviewDialog
      title={upload.label}
      mimeHint={upload.mimeType}
      downloadName={upload.label}
      load={load}
      fallbackText={`"${upload.label}" isn't available right now — the file hasn't been published to storage yet or couldn't be fetched.`}
      onClose={onClose}
    />
  );
}

function DocxPreviewPane({
  docBlob,
  label,
  objectUrl,
  downloadName,
}: {
  docBlob: Blob;
  label: string;
  objectUrl: string;
  downloadName: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let cancelled = false;
    renderDocxInto(docBlob, el).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      el.innerHTML = "";
    };
  }, [docBlob]);

  if (failed) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-3 px-6"
        style={{ background: "var(--bg-panel-alt)" }}
      >
        <span
          style={{
            color: "var(--text-muted)",
            fontSize: "var(--fs-label)",
            letterSpacing: "2px",
          }}
        >
          Inline preview not available
        </span>
        <a
          href={objectUrl}
          download={downloadName}
          className="modal-close-btn"
          style={{ fontSize: "var(--fs-label)", textDecoration: "none" }}
        >
          Download to view
        </a>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      aria-label={`Document preview of ${label}`}
      className="console-scroll h-full w-full overflow-auto"
      style={{ background: "var(--bg-panel-alt)" }}
    />
  );
}

function SimpleXlsxPane({
  sheets,
  label,
}: {
  sheets: SheetPreview[];
  label: string;
}) {
  const [active, setActive] = useState(0);
  const sheet = sheets[active] ?? sheets[0];
  if (!sheet) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
      >
        Empty workbook
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 w-full flex-col" aria-label={`Spreadsheet preview of ${label}`}>
      {sheets.length > 1 && (
        <div
          className="flex shrink-0 gap-1 overflow-x-auto px-3 py-2"
          style={{ borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)" }}
        >
          {sheets.map((s, i) => (
            <button
              key={`${s.name}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              className="rounded px-2 py-1 text-left"
              style={{
                fontSize: "var(--fs-micro)",
                color: i === active ? "var(--accent-sky)" : "var(--text-muted)",
                background:
                  i === active
                    ? "rgba(var(--accent-sky-rgb), 0.12)"
                    : "transparent",
              }}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="console-scroll min-h-0 flex-1 overflow-auto p-3">
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: "var(--fs-label)",
            color: "var(--text-secondary)",
            width: "100%",
          }}
        >
          <tbody>
            {sheet.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      border: "1px solid rgba(var(--accent-true-blue-rgb), 0.15)",
                      padding: "4px 8px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SimplePptxPane({
  slides,
  label,
}: {
  slides: SlidePreview[];
  label: string;
}) {
  return (
    <div
      className="console-scroll h-full w-full overflow-auto p-4"
      aria-label={`Presentation outline of ${label}`}
      style={{ background: "var(--bg-panel-alt)" }}
    >
      {slides.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}>
          No slide text found
        </p>
      ) : (
        <ol className="flex flex-col gap-4" style={{ margin: 0, paddingLeft: 20 }}>
          {slides.map((slide) => (
            <li key={slide.number}>
              <div
                style={{
                  fontSize: "var(--fs-label)",
                  fontWeight: 700,
                  color: "var(--accent-sky)",
                  marginBottom: 4,
                }}
              >
                Slide {slide.number}
              </div>
              {slide.lines.map((line, i) => (
                <p
                  key={i}
                  style={{
                    margin: "2px 0",
                    fontSize: "var(--fs-base)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {line}
                </p>
              ))}
              {slide.notes.length > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: "var(--fs-label)",
                    color: "var(--text-muted)",
                  }}
                >
                  Notes: {slide.notes.join(" ")}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
